import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Share2,
  Link as LinkIcon,
  Calendar,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  X,
  Phone,
  Mail,
  Copy,
  ExternalLink,
  Navigation,
  AlertCircle,
  History,
  ChevronDown,
  ChevronUp,
  DoorOpen,
  Timer,
  Clock,
  Euro,
  Gamepad2,
  Grid3x3,
  Users,
  Tags,
  User,
  Users2,
  Trophy,
  Info,
  CheckCircle2,
  UserCheck,
  Hourglass,
} from 'lucide-react';
import api from '../services/api';
import tournamentService, { Tournament } from '../services/tournamentService';
import subtournamentService, { Subtournament } from '../services/subtournamentService';
import entryService, { LightEntry, LightTeamEntry } from '../services/entryService';
import { useAuth } from '../contexts/AuthContext';
import FavoriteButton from '../components/profile/FavoriteButton';
import StartChatButton from '../components/StartChatButton';

const ACCENT = '#6366F1';
const TEAL = '#3FCED2';
const SUCCESS = '#10B981';
const WARN = '#F59E0B';
const DANGER = '#EF4444';

const TOURNAMENT_DETAILS_MOBILE_LIMIT = 3;

const MONTHS_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

function formatTimeHM(time?: { hour?: number; minute?: number } | null): string | null {
  if (!time || typeof time.hour !== 'number' || typeof time.minute !== 'number') return null;
  return `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;
}

function formatLongDateGerman(ts: number): string {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()}. ${MONTHS_DE[d.getMonth()]} ${d.getFullYear()}`;
}

function formatShortDate(ts: number): string {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

function buildFileUrl(fileId?: string | null): string | null {
  if (!fileId) return null;
  if (fileId.startsWith('http://') || fileId.startsWith('https://')) return fileId;
  const base = api.defaults.baseURL || '';
  return `${base}/api/file/${fileId}`;
}

interface SubtournamentExtended extends Subtournament {
  description?: string;
  image?: string;
  entryEnabled?: boolean;
  entryLink?: string;
  start?: { hour?: number; minute?: number };
  entryDeadlineOnSite?: { hour?: number; minute?: number };
}

const TournamentDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [competitions, setCompetitions] = useState<SubtournamentExtended[]>([]);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDescriptionExpanded, setDescExpanded] = useState(false);
  const [isDetailsExpanded, setDetailsExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [entryLinkModalOpen, setEntryLinkModalOpen] = useState(false);
  const [copyToast, setCopyToast] = useState<string | null>(null);

  // Entry-System (Anmeldung)
  const [isRegistered, setIsRegistered] = useState(false);
  const [lightEntries, setLightEntries] = useState<LightEntry[] | null>(null);
  const [lightTeamEntries, setLightTeamEntries] = useState<LightTeamEntry[] | null>(null);
  const [waitingCount, setWaitingCount] = useState<number | null>(null);

  // ===================================================================
  // Loading
  // ===================================================================
  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    try {
      const [detailRes] = await Promise.all([
        tournamentService.getTournamentBySlug(slug),
        subtournamentService.getAllSubtournaments().catch(() => []),
      ]);
      const t: Tournament | null = detailRes?.data || null;
      if (!t || typeof t !== 'object') {
        setNotFound(true);
        setTournament(null);
        setCompetitions([]);
        return;
      }
      if (t.visible === false) {
        setNotFound(true);
        setTournament(null);
        setCompetitions([]);
        return;
      }
      setTournament(t);
      const tournamentId = (t as any).id || (t as any)._id;
      if (tournamentId) {
        const subs = subtournamentService.getByMainTournament(tournamentId) as SubtournamentExtended[];
        setCompetitions(subs);
      } else {
        setCompetitions([]);
      }
    } catch (err) {
      console.error('Fehler beim Laden des Turniers:', err);
      setNotFound(true);
      setTournament(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (copyToast) {
      const tid = setTimeout(() => setCopyToast(null), 2000);
      return () => clearTimeout(tid);
    }
  }, [copyToast]);

  // ===================================================================
  // Entry-System: eigene Anmeldung + Teilnehmerliste (light) laden
  // Hinweis: Die Light-Listen-Routen erfordern Auth – daher nur für
  // eingeloggte User laden. Fehler (z. B. Liste nicht sichtbar) werden
  // still ignoriert.
  // ===================================================================
  useEffect(() => {
    let cancelled = false;
    const tournamentId = (tournament as any)?.id || (tournament as any)?._id;
    if (!tournamentId || !isAuthenticated) {
      setIsRegistered(false);
      setLightEntries(null);
      setLightTeamEntries(null);
      setWaitingCount(null);
      return;
    }

    const competitionIds = new Set(
      competitions.map((c) => (c as any).id || (c as any)._id).filter(Boolean)
    );

    const loadRegistrationState = async () => {
      try {
        const regs = await entryService.getUserRegistrations();
        const all = [...(regs.upcoming || []), ...(regs.past || [])];
        const found = all.some((r) => {
          const rid = r?.registerable?.id;
          return rid != null && (rid === tournamentId || competitionIds.has(rid));
        });
        if (!cancelled) setIsRegistered(found);
      } catch {
        /* still ignorieren */
      }
    };

    const loadParticipants = async () => {
      const type = (tournament?.type || '').toString().toUpperCase();
      const isTeamType = type === 'DOUBLE' || type === 'TEAM';
      if (isTeamType) {
        const [entries, waiting] = await Promise.all([
          entryService.getLightTeamEntries(tournamentId).catch(() => null),
          entryService.getLightTeamWaiting(tournamentId).catch(() => null),
        ]);
        if (!cancelled) {
          setLightTeamEntries(entries);
          setWaitingCount(Array.isArray(waiting) ? waiting.length : null);
        }
      } else {
        const [entries, waiting] = await Promise.all([
          entryService.getLightEntries(tournamentId).catch(() => null),
          entryService.getLightWaiting(tournamentId).catch(() => null),
        ]);
        if (!cancelled) {
          setLightEntries(entries);
          setWaitingCount(Array.isArray(waiting) ? waiting.length : null);
        }
      }
    };

    loadRegistrationState();
    loadParticipants();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament, competitions, isAuthenticated]);

  // ===================================================================
  // Derived
  // ===================================================================
  const allImages = useMemo<string[]>(() => {
    if (!tournament) return [];
    const out: string[] = [];
    const mainUrl = buildFileUrl(tournament.image);
    if (mainUrl) out.push(mainUrl);
    if (Array.isArray(tournament.images)) {
      for (const imgId of tournament.images) {
        const url = buildFileUrl(imgId);
        if (url) out.push(url);
      }
    }
    return out;
  }, [tournament]);

  const hasImages = allImages.length > 0;

  const allDates = useMemo<number[]>(() => {
    if (!tournament) return [];
    return tournamentService.calcDates(tournament).slice().sort((a, b) => a - b);
  }, [tournament]);

  const formattedLongDates = useMemo<string>(() => {
    if (allDates.length === 0) return 'Kein Datum';
    const parts = allDates.map(formatLongDateGerman);
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} & ${parts[1]}`;
    return parts.join(', ');
  }, [allDates]);

  const shortDateRange = useMemo<string>(() => {
    if (allDates.length === 0) return 'Kein Datum';
    if (allDates.length === 1) return formatShortDate(allDates[0]);
    const first = new Date(allDates[0]);
    const last = new Date(allDates[allDates.length - 1]);
    const f = `${String(first.getDate()).padStart(2, '0')}.${String(first.getMonth() + 1).padStart(2, '0')}.`;
    const l = `${String(last.getDate()).padStart(2, '0')}.${String(last.getMonth() + 1).padStart(2, '0')}.${last.getFullYear()}`;
    return `${f} - ${l}`;
  }, [allDates]);

  const validDates = useMemo<boolean>(() => {
    if (allDates.length === 0) return false;
    const now = new Date();
    const todayTs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return allDates.some((d) => d >= todayTs);
  }, [allDates]);

  const address = tournament?.address;
  const addressLabel = useMemo<string>(() => {
    if (!address) return 'Keine Adresse';
    if (address.label && address.label.trim()) return address.label;
    return tournamentService.formatAddress(address);
  }, [address]);

  // ===================================================================
  // Tournament Details Items
  // ===================================================================
  const detailItems = useMemo(() => {
    if (!tournament) return [] as { label: string; value: string; Icon: React.ComponentType<any> }[];
    const items: { label: string; value: string; Icon: React.ComponentType<any> }[] = [];

    if (allDates.length > 0) {
      items.push({
        label: 'Datum',
        value: allDates.map(formatShortDate).join('\n'),
        Icon: Calendar,
      });
    }
    const doors = formatTimeHM((tournament as any).doorsOpen);
    if (doors) items.push({ label: 'Einlass ab', value: `${doors} Uhr`, Icon: DoorOpen });
    const deadlineOnSite = formatTimeHM((tournament as any).entryDeadlineOnSite);
    if (deadlineOnSite) items.push({ label: 'Anmeldeschluss vor Ort', value: `${deadlineOnSite} Uhr`, Icon: Timer });
    const start = formatTimeHM(tournament.start);
    if (start) items.push({ label: 'Turnierbeginn', value: `${start} Uhr`, Icon: Clock });
    if (typeof tournament.entryFee === 'number') {
      items.push({
        label: 'Startgeld',
        value: `${tournament.entryFee.toFixed(2).replace('.', ',')} €`,
        Icon: Euro,
      });
    }
    if (tournament.gameMode && tournament.gameMode.trim()) {
      items.push({ label: 'Spielmodus', value: tournament.gameMode, Icon: Gamepad2 });
    }
    if (typeof (tournament as any).boards === 'number') {
      items.push({ label: 'Boards', value: `${(tournament as any).boards} Boards`, Icon: Grid3x3 });
    }
    if (typeof tournament.entryLimit === 'number') {
      items.push({ label: 'Teilnehmerlimit', value: `${tournament.entryLimit} Plätze`, Icon: Users });
    }
    if (Array.isArray(tournament.categories) && tournament.categories.length > 0) {
      const labels = tournament.categories.map((c) => {
        const u = (c || '').toString().toUpperCase();
        if (u === 'STEELDART') return 'Steeldart';
        if (u === 'EDART') return 'E-Dart';
        if (u === 'ONLINE') return 'Online';
        return c;
      });
      items.push({ label: 'Kategorie', value: labels.join(', '), Icon: Tags });
    }
    return items;
  }, [tournament, allDates]);

  // ===================================================================
  // Image Nav
  // ===================================================================
  const previousImage = useCallback(() => {
    if (allImages.length === 0) return;
    setCurrentImageIndex((i) => (i - 1 + allImages.length) % allImages.length);
  }, [allImages.length]);

  const nextImage = useCallback(() => {
    if (allImages.length === 0) return;
    setCurrentImageIndex((i) => (i + 1) % allImages.length);
  }, [allImages.length]);

  // ===================================================================
  // Actions
  // ===================================================================
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/turnier/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopyToast('Link kopiert');
    } catch {
      setCopyToast('Konnte nicht kopieren');
    }
  };

  const handleShare = async () => {
    if (!tournament) return;
    const url = `${window.location.origin}/turnier/${slug}`;
    const title = tournament.name || 'Turnier';
    if (navigator.share) {
      try {
        await navigator.share({ title, text: `${title} auf DartCloud`, url });
        return;
      } catch {
        /* user cancelled, fallback */
      }
    }
    await navigator.clipboard.writeText(url).catch(() => {});
    setCopyToast('Link kopiert');
  };

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyToast(`${label} kopiert`);
    } catch {
      setCopyToast('Konnte nicht kopieren');
    }
  };

  const handleOpenMaps = () => {
    if (!address) return;
    const lat = address.latitude;
    const lng = address.longitude;
    if (typeof lat === 'number' && typeof lng === 'number') {
      window.open(`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`, '_blank', 'noopener,noreferrer');
    } else {
      const q = encodeURIComponent(addressLabel);
      window.open(`https://www.openstreetmap.org/search?query=${q}`, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCallPhone = () => {
    if (!tournament?.phone) return;
    window.location.href = `tel:${tournament.phone.replace(/\s+/g, '')}`;
  };

  const handleSendEmail = () => {
    if (!tournament?.email) return;
    window.location.href = `mailto:${tournament.email}`;
  };

  const handleOpenEntryLink = () => {
    setEntryLinkModalOpen(true);
  };

  const handleConfirmEntryLink = () => {
    if (tournament?.entryLink) {
      window.open(tournament.entryLink, '_blank', 'noopener,noreferrer');
    }
    setEntryLinkModalOpen(false);
  };

  // ===================================================================
  // Render
  // ===================================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 py-20">
        <div className="h-10 w-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
        <p className="mt-4 text-sm text-gray-400">Turnier laden…</p>
      </div>
    );
  }

  if (notFound || !tournament) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6 py-20 text-center">
        <div className="h-16 w-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
          <AlertCircle size={32} className="text-gray-400" />
        </div>
        <h1 className="text-xl font-bold text-white mb-2">Turnier nicht gefunden</h1>
        <p className="text-sm text-gray-400 mb-6">Das angeforderte Turnier existiert nicht oder ist nicht öffentlich.</p>
        <button
          onClick={() => navigate('/turniere')}
          className="px-4 py-2 rounded-lg bg-white text-black font-medium text-sm hover:bg-gray-200 transition"
        >
          Zur Turnierliste
        </button>
      </div>
    );
  }

  const cancelled = tournament.cancelled === true;
  const isPast = !cancelled && !validDates;
  const tournamentId: string = (tournament as any).id || (tournament as any)._id || '';

  // DartCloud-Anmeldung aktiv? (wie Flutter: registrationType DARTCLOUD oder entryEnabled ohne registrationType)
  const registrationType = ((tournament as any).registrationType || '').toString().toUpperCase();
  const isDartCloudRegistration =
    registrationType === 'DARTCLOUD' || (tournament.entryEnabled === true && !registrationType);
  const showRegistrationCta = !cancelled && !isPast && (isDartCloudRegistration || Boolean(tournament.entryLink));
  const registrationDeadlinePassed =
    typeof tournament.entryDeadline === 'number' &&
    tournament.entryDeadline > 0 &&
    Date.now() > tournament.entryDeadline;

  const participantCount =
    lightEntries != null ? lightEntries.length : lightTeamEntries != null ? lightTeamEntries.length : null;
  const hasParticipantSection = participantCount != null;
  const description = (tournament.description || '').trim();
  const longDesc = description.length > 300;
  const displayedDesc = !longDesc || isDescriptionExpanded ? description : `${description.substring(0, 300)}…`;

  const metaDescription = description.length > 0
    ? (description.length > 160 ? `${description.substring(0, 157)}...` : description)
    : `${tournament.name || 'Turnier'} - Dart-Turnier am ${formattedLongDates} in ${addressLabel}. Alle Infos auf DartCloud.`;
  const canonicalUrl = `https://dartcloudapp.de/turnier/${slug}`;
  const ogImage = hasImages ? allImages[0] : undefined;

  const detailsToShowOnMobile = detailItems.slice(0, TOURNAMENT_DETAILS_MOBILE_LIMIT);
  const hasMoreDetails = detailItems.length > TOURNAMENT_DETAILS_MOBILE_LIMIT;

  return (
    <>
      <Helmet>
        <title>{`${tournament.name || 'Turnier'} | DartCloud`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${tournament.name || 'Turnier'} | DartCloud`} />
        <meta property="og:description" content={metaDescription} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen bg-black text-white overflow-x-hidden">
        {/* ============ HERO ============ */}
        <section className="border-b border-zinc-800 bg-zinc-900/40">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-5 sm:py-8 md:py-10">
            {/* Top row: back + actions */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <button
                onClick={handleBack}
                aria-label="Zurück"
                className="h-10 w-10 sm:h-11 sm:w-11 flex items-center justify-center rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 transition"
              >
                <ArrowLeft size={18} />
              </button>
              <div className="flex items-center gap-2 sm:gap-3">
                <FavoriteButton
                  id={tournamentId}
                  type="TOURNAMENT"
                  size={18}
                  className="h-10 w-10 sm:h-11 sm:w-11 justify-center rounded-xl border bg-zinc-800 hover:bg-zinc-700 border-zinc-700"
                />
                <ActionButton Icon={Share2} onClick={handleShare} ariaLabel="Teilen" />
                <ActionButton Icon={LinkIcon} onClick={handleCopyLink} ariaLabel="Link kopieren" hiddenOnMobile />
              </div>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold tracking-tight leading-tight text-white mb-3 sm:mb-4 break-words">
              {tournament.name}
            </h1>

            <div className="flex flex-wrap gap-2 sm:gap-3">
              <HeroTag Icon={Calendar} label={shortDateRange} />
              <HeroTag Icon={MapPin} label={addressLabel} />
            </div>
          </div>
        </section>

        {/* ============ MAIN ============ */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-8">
            {/* ---- Left: Main Content ---- */}
            <div className="min-w-0">
              {/* Cancelled banner */}
              {cancelled && (
                <Banner
                  Icon={X}
                  title="Turnier abgesagt"
                  text="Dieses Turnier wurde vom Veranstalter abgesagt."
                  color={DANGER}
                />
              )}

              {/* Past banner */}
              {isPast && (
                <Banner
                  Icon={History}
                  title="Turnier beendet"
                  text="Dieses Turnier hat bereits stattgefunden."
                  color="#9CA3AF"
                />
              )}

              {/* Bereits angemeldet */}
              {isRegistered && (
                <div
                  className="mb-6 p-4 rounded-xl border flex flex-wrap items-center gap-3"
                  style={{ borderColor: `${SUCCESS}55`, backgroundColor: `${SUCCESS}1A` }}
                >
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${SUCCESS}33` }}
                  >
                    <UserCheck size={20} style={{ color: SUCCESS }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-bold" style={{ color: SUCCESS }}>Du bist angemeldet</p>
                    <p className="text-sm text-gray-400 mt-0.5">Du bist für dieses Turnier bereits angemeldet.</p>
                  </div>
                  <Link
                    to="/meine-anmeldungen"
                    className="px-4 py-2 rounded-lg text-sm font-semibold border transition hover:bg-white/5"
                    style={{ color: SUCCESS, borderColor: `${SUCCESS}55` }}
                  >
                    Anmeldung verwalten
                  </Link>
                </div>
              )}

              {/* Anmelde-CTA */}
              {showRegistrationCta && !isRegistered && (
                <div className="mb-8 p-5 sm:p-6 rounded-2xl border" style={{ borderColor: `${TEAL}44`, backgroundColor: `${TEAL}0D` }}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-lg font-bold text-white">Anmeldung geöffnet</p>
                      <p className="text-sm text-gray-400 mt-1">
                        {isDartCloudRegistration && registrationDeadlinePassed
                          ? `Die Online-Anmeldefrist ist am ${formatShortDate(tournament.entryDeadline as number)} abgelaufen.`
                          : isDartCloudRegistration
                            ? 'Melde dich jetzt direkt über DartCloud für dieses Turnier an.'
                            : 'Die Anmeldung erfolgt über eine externe Website.'}
                      </p>
                    </div>
                    {isDartCloudRegistration ? (
                      registrationDeadlinePassed ? null : (
                        <Link
                          to={`/turnier/${slug}/anmelden`}
                          className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-black transition hover:opacity-90"
                          style={{ backgroundColor: TEAL }}
                        >
                          <CheckCircle2 size={18} />
                          Jetzt anmelden
                        </Link>
                      )
                    ) : (
                      <button
                        onClick={handleOpenEntryLink}
                        className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-black transition hover:opacity-90"
                        style={{ backgroundColor: TEAL }}
                      >
                        <ExternalLink size={18} />
                        Zur Anmeldung
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Image Gallery */}
              {hasImages && (
                <div className="mb-8">
                  <ImageGallery
                    images={allImages}
                    currentIndex={currentImageIndex}
                    onPrev={previousImage}
                    onNext={nextImage}
                    onSelectIndex={setCurrentImageIndex}
                    onOpenLightbox={() => setLightboxOpen(true)}
                    tournamentName={tournament.name || 'Turnier'}
                  />
                </div>
              )}

              {/* Description */}
              {description.length > 0 && (
                <Section title="Über das Turnier">
                  <p className="text-[15px] sm:text-base leading-[1.8] text-gray-300 whitespace-pre-line">
                    {displayedDesc}
                  </p>
                  {longDesc && (
                    <button
                      onClick={() => setDescExpanded((v) => !v)}
                      className="mt-3 inline-flex items-center gap-1 text-sm font-semibold"
                      style={{ color: ACCENT }}
                    >
                      {isDescriptionExpanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                      {isDescriptionExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  )}
                </Section>
              )}

              {/* Tournament Details (only if no competitions) */}
              {competitions.length === 0 && detailItems.length > 0 && (
                <Section title="Turnierdetails">
                  <DetailItemsList
                    items={detailItems}
                    showAll={isDetailsExpanded}
                    mobileLimit={TOURNAMENT_DETAILS_MOBILE_LIMIT}
                  />
                  {hasMoreDetails && (
                    <div className="mt-4 lg:hidden">
                      <button
                        onClick={() => setDetailsExpanded((v) => !v)}
                        className="w-full py-3 px-4 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition flex items-center justify-center gap-2"
                      >
                        <span className="text-sm font-semibold" style={{ color: ACCENT }}>
                          {isDetailsExpanded ? 'Weniger anzeigen' : 'Mehr Details anzeigen'}
                        </span>
                        {!isDetailsExpanded && (
                          <span className="text-xs text-gray-400">
                            ({detailItems.length - detailsToShowOnMobile.length} weitere)
                          </span>
                        )}
                        {isDetailsExpanded ? (
                          <ChevronUp size={18} style={{ color: ACCENT }} />
                        ) : (
                          <ChevronDown size={18} style={{ color: ACCENT }} />
                        )}
                      </button>
                    </div>
                  )}
                </Section>
              )}

              {/* Competitions */}
              {competitions.length > 0 && (
                <Section title="Competitions">
                  <div className="space-y-4">
                    {competitions.map((comp) => (
                      <CompetitionCard key={comp.id || comp._id} comp={comp} slug={slug || ''} />
                    ))}
                  </div>
                </Section>
              )}

              {/* Teilnehmerliste (light) */}
              {hasParticipantSection && (
                <Section title={`Teilnehmer (${participantCount})`}>
                  {waitingCount != null && waitingCount > 0 && (
                    <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-sm text-gray-300">
                      <Hourglass size={14} style={{ color: WARN }} />
                      {waitingCount} auf der Warteliste
                    </div>
                  )}

                  {lightEntries != null && (
                    lightEntries.length === 0 ? (
                      <p className="text-sm text-gray-400">
                        Noch keine Anmeldungen – sei der Erste und melde dich an!
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {lightEntries.map((entry, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-zinc-900 border border-zinc-800"
                          >
                            <span
                              className="h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
                              style={{ backgroundColor: `${TEAL}22`, color: TEAL }}
                            >
                              {idx + 1}
                            </span>
                            <span className="text-sm text-gray-200 truncate">
                              {`${entry.firstname || ''} ${entry.lastname || ''}`.trim() || 'Teilnehmer'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )
                  )}

                  {lightTeamEntries != null && (
                    lightTeamEntries.length === 0 ? (
                      <p className="text-sm text-gray-400">
                        Noch keine Team-Anmeldungen – meldet euer Team jetzt an!
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {lightTeamEntries.map((team, idx) => (
                          <div key={idx} className="p-3.5 rounded-xl bg-zinc-900 border border-zinc-800">
                            <div className="flex items-center gap-2.5 mb-1.5">
                              <span
                                className="h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
                                style={{ backgroundColor: `${TEAL}22`, color: TEAL }}
                              >
                                {idx + 1}
                              </span>
                              <span className="text-sm font-semibold text-white truncate">
                                {team.teamName || `Team ${idx + 1}`}
                              </span>
                            </div>
                            {Array.isArray(team.players) && team.players.length > 0 && (
                              <p className="text-xs text-gray-400 ml-9">
                                {team.players
                                  .map((p) => `${p?.firstname || ''} ${p?.lastname || ''}`.trim())
                                  .filter(Boolean)
                                  .join(', ')}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </Section>
              )}
            </div>

            {/* ---- Right: Sidebar ---- */}
            <aside className="space-y-5">
              {/* Location */}
              <SidebarCard title="Veranstaltungsort">
                <div className="flex items-start gap-2 mb-4">
                  <MapPin size={18} className="shrink-0 mt-0.5" style={{ color: ACCENT }} />
                  <span className="text-sm text-gray-300 leading-relaxed break-words">{addressLabel}</span>
                </div>
                <MapPreview
                  lat={address?.latitude}
                  lng={address?.longitude}
                  label={addressLabel}
                  onOpen={handleOpenMaps}
                />
              </SidebarCard>

              {/* Contact */}
              {(tournament.phone || tournament.email) && (
                <SidebarCard title="Kontaktdaten">
                  <div className="space-y-2">
                    {tournament.phone && (
                      <ContactItem
                        Icon={Phone}
                        label={tournament.phone}
                        onClick={handleCallPhone}
                        onCopy={() => handleCopy(tournament.phone!, 'Telefonnummer')}
                      />
                    )}
                    {tournament.email && (
                      <ContactItem
                        Icon={Mail}
                        label={tournament.email}
                        onClick={handleSendEmail}
                        onCopy={() => handleCopy(tournament.email!, 'E-Mail')}
                      />
                    )}
                  </div>
                </SidebarCard>
              )}

              {/* DartCloud-Anmeldung */}
              {isDartCloudRegistration && !cancelled && !isPast && !registrationDeadlinePassed && (
                <SidebarCard title="Turnieranmeldung">
                  <p className="text-sm text-gray-400 mb-4">
                    {isRegistered
                      ? 'Du bist für dieses Turnier bereits angemeldet.'
                      : 'Melde dich direkt über DartCloud für dieses Turnier an.'}
                  </p>
                  {isRegistered ? (
                    <Link
                      to="/meine-anmeldungen"
                      className="w-full py-3 px-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 border transition hover:bg-white/5"
                      style={{ color: SUCCESS, borderColor: `${SUCCESS}55` }}
                    >
                      <UserCheck size={16} />
                      Anmeldung verwalten
                    </Link>
                  ) : (
                    <Link
                      to={`/turnier/${slug}/anmelden`}
                      className="w-full py-3 px-4 rounded-xl font-semibold text-black inline-flex items-center justify-center gap-2 transition hover:opacity-90"
                      style={{ backgroundColor: TEAL }}
                    >
                      <CheckCircle2 size={16} />
                      Jetzt anmelden
                    </Link>
                  )}
                </SidebarCard>
              )}

              {/* Entry Link */}
              {!isDartCloudRegistration && tournament.entryLink && tournament.entryLink.trim().length > 0 && (
                <SidebarCard title="Turnieranmeldung">
                  <p className="text-sm text-gray-400 mb-3">
                    Die Anmeldung für dieses Turnier erfolgt über eine externe Website.
                  </p>
                  <p className="text-xs text-gray-500 mb-4 break-all line-clamp-1">{tournament.entryLink}</p>
                  <button
                    onClick={handleOpenEntryLink}
                    className="w-full py-3 px-4 rounded-xl font-semibold text-black inline-flex items-center justify-center gap-2 transition hover:opacity-90"
                    style={{ backgroundColor: TEAL }}
                  >
                    <ExternalLink size={16} />
                    Zur Anmeldung
                  </button>
                </SidebarCard>
              )}

              {/* Nachricht / Chat */}
              {tournamentId && (
                <SidebarCard title="Nachricht">
                  <p className="text-sm text-gray-400 mb-4">
                    Hast du Fragen zum Turnier? Sende eine Nachricht über DartCloud.
                  </p>
                  <StartChatButton entryId={tournamentId} entryType="TOURNAMENT" />
                </SidebarCard>
              )}
            </aside>
          </div>
        </div>

        {/* ============ Lightbox ============ */}
        {lightboxOpen && hasImages && (
          <Lightbox
            images={allImages}
            currentIndex={currentImageIndex}
            onClose={() => setLightboxOpen(false)}
            onPrev={previousImage}
            onNext={nextImage}
          />
        )}

        {/* ============ Entry Link Modal ============ */}
        {entryLinkModalOpen && (
          <ExternalLinkModal
            onCancel={() => setEntryLinkModalOpen(false)}
            onConfirm={handleConfirmEntryLink}
          />
        )}

        {/* ============ Copy Toast ============ */}
        {copyToast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl bg-zinc-900 border border-zinc-700 shadow-xl flex items-center gap-2 text-sm">
            <CheckCircle2 size={16} style={{ color: SUCCESS }} />
            {copyToast}
          </div>
        )}
      </div>
    </>
  );
};

// =====================================================================
// Sub-Components
// =====================================================================

const ActionButton: React.FC<{
  Icon: React.ComponentType<any>;
  onClick: () => void;
  ariaLabel: string;
  active?: boolean;
  hiddenOnMobile?: boolean;
}> = ({ Icon, onClick, ariaLabel, active = false, hiddenOnMobile = false }) => (
  <button
    onClick={onClick}
    aria-label={ariaLabel}
    className={
      'h-10 w-10 sm:h-11 sm:w-11 flex items-center justify-center rounded-xl border transition ' +
      (active
        ? 'bg-[#6366F1]/15 border-[#6366F1] text-[#6366F1]'
        : 'bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-gray-200 ') +
      (hiddenOnMobile ? ' hidden sm:flex' : '')
    }
  >
    <Icon size={18} />
  </button>
);

const HeroTag: React.FC<{ Icon: React.ComponentType<any>; label: string }> = ({ Icon, label }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-800 border border-zinc-700 text-xs sm:text-sm">
    <Icon size={14} style={{ color: ACCENT }} />
    <span className="text-gray-300 truncate max-w-[260px] sm:max-w-none">{label}</span>
  </div>
);

const Banner: React.FC<{
  Icon: React.ComponentType<any>;
  title: string;
  text: string;
  color: string;
}> = ({ Icon, title, text, color }) => (
  <div
    className="mb-6 p-4 rounded-xl border flex items-start gap-3"
    style={{ borderColor: `${color}55`, backgroundColor: `${color}1A` }}
  >
    <div
      className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
      style={{ backgroundColor: `${color}33` }}
    >
      <Icon size={20} style={{ color }} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-base font-bold" style={{ color }}>{title}</p>
      <p className="text-sm text-gray-400 mt-0.5">{text}</p>
    </div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-4">{title}</h2>
    {children}
  </section>
);

const DetailItemsList: React.FC<{
  items: { label: string; value: string; Icon: React.ComponentType<any> }[];
  showAll: boolean;
  mobileLimit: number;
}> = ({ items, showAll, mobileLimit }) => {
  return (
    <div className="space-y-3">
      {items.map((item, idx) => {
        const hiddenOnMobile = !showAll && idx >= mobileLimit;
        return (
          <div
            key={`${item.label}-${idx}`}
            className={
              'flex items-start gap-4 p-4 rounded-xl bg-zinc-900 border border-zinc-800 ' +
              (hiddenOnMobile ? 'hidden lg:flex' : 'flex')
            }
          >
            <div
              className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${ACCENT}1A` }}
            >
              <item.Icon size={20} style={{ color: ACCENT }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-400 mb-1">{item.label}</p>
              <p className="text-base font-semibold text-white whitespace-pre-line break-words">{item.value}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ImageGallery: React.FC<{
  images: string[];
  currentIndex: number;
  onPrev: () => void;
  onNext: () => void;
  onSelectIndex: (idx: number) => void;
  onOpenLightbox: () => void;
  tournamentName: string;
}> = ({ images, currentIndex, onPrev, onNext, onSelectIndex, onOpenLightbox, tournamentName }) => {
  const safeIdx = currentIndex >= images.length ? 0 : currentIndex;
  return (
    <div>
      <div className="relative rounded-2xl overflow-hidden bg-zinc-900 aspect-[16/10] sm:aspect-[16/9]">
        <button
          onClick={onOpenLightbox}
          className="absolute inset-0 group focus:outline-none"
          aria-label="Bild vergrößern"
        >
          <img
            src={images[safeIdx]}
            alt={`${tournamentName} - Turnierbild ${safeIdx + 1}`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute bottom-3 left-3 p-2 rounded-md bg-black/50 group-hover:bg-black/70 transition">
            <Maximize2 size={18} />
          </div>
        </button>

        {images.length > 1 && (
          <>
            <button
              onClick={onPrev}
              aria-label="Vorheriges Bild"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={onNext}
              aria-label="Nächstes Bild"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition"
            >
              <ChevronRight size={22} />
            </button>
            <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-black/60 text-xs font-medium">
              {safeIdx + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="mt-4 flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
          {images.map((img, idx) => {
            const active = idx === safeIdx;
            return (
              <button
                key={img}
                onClick={() => onSelectIndex(idx)}
                className={
                  'h-20 w-28 shrink-0 rounded-xl overflow-hidden border-2 transition ' +
                  (active
                    ? 'border-[#6366F1]'
                    : 'border-zinc-800 hover:border-zinc-600')
                }
                aria-label={`Bild ${idx + 1} anzeigen`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Lightbox: React.FC<{
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}> = ({ images, currentIndex, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext]);

  const idx = currentIndex >= images.length ? 0 : currentIndex;

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center px-4 py-4">
      <button
        onClick={onClose}
        aria-label="Schließen"
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
      >
        <X size={20} />
      </button>
      <img src={images[idx]} alt="" className="max-h-full max-w-full object-contain" />
      {images.length > 1 && (
        <>
          <button
            onClick={onPrev}
            aria-label="Vorheriges Bild"
            className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
          >
            <ChevronLeft size={26} />
          </button>
          <button
            onClick={onNext}
            aria-label="Nächstes Bild"
            className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
          >
            <ChevronRight size={26} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-xs">
            {idx + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
};

const SidebarCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="p-5 sm:p-6 rounded-2xl bg-zinc-900 border border-zinc-800">
    <h3 className="text-lg font-bold text-white mb-5">{title}</h3>
    {children}
  </div>
);

const MapPreview: React.FC<{
  lat?: number;
  lng?: number;
  label: string;
  onOpen: () => void;
}> = ({ lat, lng, onOpen }) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') {
    return (
      <div className="h-44 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center">
        <div className="text-center px-4">
          <MapPin size={28} className="mx-auto text-gray-500 mb-2" />
          <p className="text-xs text-gray-500">Kein Standort verfügbar</p>
        </div>
      </div>
    );
  }
  const d = 0.005;
  const bbox = `${lng - d}%2C${lat - d}%2C${lng + d}%2C${lat + d}`;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;
  return (
    <button
      onClick={onOpen}
      className="block w-full relative rounded-xl overflow-hidden border border-zinc-700 group"
      aria-label="In Karte öffnen"
    >
      <iframe
        title="Karte"
        src={src}
        className="w-full h-44 pointer-events-none"
        loading="lazy"
      />
      <div className="absolute inset-x-0 bottom-0 px-3 py-2 bg-gradient-to-t from-black/80 to-transparent flex items-center justify-center gap-2 text-xs font-medium">
        <Navigation size={14} />
        In Maps öffnen
      </div>
    </button>
  );
};

const ContactItem: React.FC<{
  Icon: React.ComponentType<any>;
  label: string;
  onClick: () => void;
  onCopy: () => void;
}> = ({ Icon, label, onClick, onCopy }) => (
  <div className="flex items-center gap-3 py-1">
    <Icon size={18} style={{ color: ACCENT }} className="shrink-0" />
    <button
      onClick={onClick}
      className="flex-1 min-w-0 text-left text-sm text-gray-300 hover:text-white transition truncate"
    >
      {label}
    </button>
    <button
      onClick={onCopy}
      aria-label="Kopieren"
      className="p-1.5 rounded-md text-gray-500 hover:text-gray-300 hover:bg-zinc-800 transition shrink-0"
    >
      <Copy size={14} />
    </button>
  </div>
);

const CompetitionCard: React.FC<{ comp: SubtournamentExtended; slug: string }> = ({ comp }) => {
  const type = (comp.type || '').toString().toUpperCase();
  const typeLabel = type === 'SINGLE' ? 'Einzel' : type === 'DOUBLE' ? 'Doppel' : type === 'TEAM' ? 'Team' : '—';
  const TypeIcon = type === 'SINGLE' ? User : type === 'DOUBLE' ? Users2 : type === 'TEAM' ? Trophy : Info;

  const cats: string[] = Array.isArray(comp.categories) ? comp.categories : [];
  const compStart = formatTimeHM(comp.start);
  const compDeadline = formatTimeHM(comp.entryDeadlineOnSite);
  const imageUrl = buildFileUrl(comp.image);

  return (
    <div className="rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden">
      {imageUrl && (
        <div className="aspect-[16/8] bg-zinc-800">
          <img src={imageUrl} alt={comp.name || ''} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${ACCENT}1A` }}
          >
            <TypeIcon size={20} style={{ color: ACCENT }} />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-[17px] font-bold text-white break-words">{comp.name || 'Competition'}</h4>
            {comp.description && (
              <p className="text-sm text-gray-400 mt-1 line-clamp-2">{comp.description}</p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <Chip label={typeLabel} Icon={Gamepad2} />
          {cats.map((c) => {
            const u = (c || '').toUpperCase();
            const label = u === 'STEELDART' ? 'Steeldart' : u === 'EDART' ? 'E-Dart' : u === 'ONLINE' ? 'Online' : c;
            return <Chip key={c} label={label} />;
          })}
          {typeof comp.entryFee === 'number' && (
            <Chip label={`${comp.entryFee.toFixed(0)}€`} Icon={Euro} />
          )}
          {typeof comp.entryLimit === 'number' && (
            <Chip label={`Max. ${comp.entryLimit}`} Icon={Users} />
          )}
        </div>

        {(compStart || compDeadline) && (
          <div className="flex flex-wrap items-center gap-4 mb-3 text-sm text-gray-300">
            {compStart && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} className="text-gray-400" />
                {compStart} Uhr
              </span>
            )}
            {compDeadline && (
              <span className="inline-flex items-center gap-1.5">
                <Timer size={14} className="text-gray-400" />
                Meldeschluss {compDeadline} Uhr
              </span>
            )}
          </div>
        )}

        {comp.entryEnabled ? (
          <button
            onClick={() => {
              window.alert('Anmeldung ist aktuell nur in der App möglich.');
            }}
            className="w-full py-2.5 px-4 rounded-xl font-semibold text-white inline-flex items-center justify-center gap-2 transition hover:opacity-90"
            style={{ backgroundColor: TEAL }}
          >
            <CheckCircle2 size={16} />
            Anmelden
          </button>
        ) : comp.entryLink ? (
          <a
            href={comp.entryLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-2.5 px-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 border transition"
            style={{ color: WARN, borderColor: WARN }}
          >
            <ExternalLink size={16} />
            Externe Anmeldung
          </a>
        ) : null}
      </div>
    </div>
  );
};

const Chip: React.FC<{ label: string; Icon?: React.ComponentType<any> }> = ({ label, Icon }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-zinc-800 border border-zinc-700 text-gray-300">
    {Icon && <Icon size={12} className="text-gray-400" />}
    {label}
  </span>
);

const ExternalLinkModal: React.FC<{ onCancel: () => void; onConfirm: () => void }> = ({ onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4">
    <div className="w-full max-w-md rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden">
      <div className="p-6 text-center" style={{ backgroundColor: `${WARN}1A` }}>
        <div
          className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-3"
          style={{ backgroundColor: `${WARN}33` }}
        >
          <ExternalLink size={26} style={{ color: WARN }} />
        </div>
        <p className="text-lg font-bold text-white">Externe Weiterleitung</p>
      </div>
      <div className="p-6">
        <p className="text-sm text-gray-300 text-center mb-4">
          Du wirst zu einer externen Webseite weitergeleitet, um dich für dieses Turnier anzumelden.
        </p>
        <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800 mb-5">
          <Info size={18} className="text-gray-400 shrink-0 mt-0.5" />
          <p className="text-xs text-gray-400">
            DartCloud ist nicht für den Inhalt externer Webseiten verantwortlich.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 rounded-lg font-semibold border border-zinc-700 text-gray-200 hover:bg-zinc-800 transition"
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 rounded-lg font-semibold text-black transition hover:opacity-90"
            style={{ backgroundColor: TEAL }}
          >
            Weiter
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default TournamentDetailPage;
